// RESERVIERUNG-GRUND-ZEIGEN-001 — „Kein Fahrer kommt in Frage" ist keine Antwort.
//
// Der Server kennt den Grund JE FAHRER (`fleet_alert.driver_alerts[].reason`, erzeugt aus
// derselben Eligibility, die auch Liste und Claim benutzen). Bisher hat das Portal nur den
// Sammelzustand gezeigt und die Gründe liegen lassen. Hier werden sie übersetzt — und vor
// allem eingeordnet: Der Partner muss nach EINEM Blick wissen, ob ER etwas tun kann.
//
// Diese Datei ändert NICHTS an der Zuweisungslogik. Sie liest nur, was
// RESERVIERUNG-AN-ALLE-001 ohnehin schon entscheidet.

// Die vier Klassen aus der Task-Karte. Reihenfolge = Anzeigereihenfolge:
// Was der Partner selbst beheben kann, steht oben.
export const REMEDY = {
  PARTNER: 'partner_action',
  TIME: 'wait',
  SYSTEM: 'system',
  ABSENT: 'nobody',
};

export const REMEDY_ORDER = [REMEDY.PARTNER, REMEDY.TIME, REMEDY.SYSTEM, REMEDY.ABSENT];

export const REMEDY_LABEL = {
  [REMEDY.PARTNER]: 'Das kannst du selbst beheben',
  [REMEDY.TIME]: 'Das löst sich mit der Zeit',
  [REMEDY.SYSTEM]: 'Das liegt gerade am System',
  [REMEDY.ABSENT]: 'Da ist gerade niemand',
};

// Ein Eintrag je Grund, den der Sammelruf erzeugen kann.
//   text  — was mit dem Fahrer los ist (wird mit „1 Fahrer" / „N Fahrer" davor gelesen)
//   hint  — was der Partner JETZT tun kann; null, wenn es nichts zu tun gibt
//
// „Gezielt zuweisen" ist bei Ablehnung und Stufen-Verzögerung ein echter Ausweg und keine
// Vertröstung: der Ablehnungs-Marker gilt ausdrücklich NUR für die freie Flottenfreigabe
// (RES-REASSIGN-001), und die Stufen-Verzögerung hängt allein am Flotten-Zweig der
// Eligibility — eine gezielte Zuweisung läuft an beidem vorbei.
const REASONS = {
  // ── Der Partner kann handeln ────────────────────────────────────────────────
  driver_rejected: {
    remedy: REMEDY.PARTNER,
    text: 'hat diese Reservierung bereits abgelehnt',
    hint: 'Du kannst sie ihm trotzdem gezielt zuweisen — das schlägt die Ablehnung.',
  },
  cash_not_enabled: {
    remedy: REMEDY.PARTNER,
    text: 'hat keine Barzahlung aktiv, diese Fahrt wird bar bezahlt',
    hint: 'Barzahlung für den Fahrer freischalten.',
  },
  vehicle_class_mismatch: {
    remedy: REMEDY.PARTNER,
    text: 'fährt ein Fahrzeug, das nicht zu dieser Fahrtkategorie passt',
    hint: 'Passendes Fahrzeug zuweisen.',
  },
  not_approved: {
    remedy: REMEDY.PARTNER,
    text: 'ist noch nicht freigegeben',
    hint: 'Freigabe abschließen.',
  },
  account_inactive: {
    remedy: REMEDY.PARTNER,
    text: 'hat ein gesperrtes oder inaktives Konto',
    hint: 'Konto klären, dann erneut freigeben.',
  },
  document_expired: {
    remedy: REMEDY.PARTNER,
    text: 'hat ein abgelaufenes Pflichtdokument',
    hint: 'Gültiges Dokument hochladen.',
  },
  vehicle_document_expired: {
    remedy: REMEDY.PARTNER,
    text: 'hat ein fehlendes, abgelaufenes oder gesperrtes Fahrzeugdokument',
    hint: 'Fahrzeugdokument erneuern — danach wird das Fahrzeug automatisch wieder freigeschaltet.',
  },
  no_vehicle: {
    remedy: REMEDY.PARTNER,
    text: 'hat kein Fahrzeug ausgewählt',
    hint: 'Fahrzeug zuweisen.',
  },
  vehicle_not_approved: {
    remedy: REMEDY.PARTNER,
    text: 'fährt ein Fahrzeug, das nicht freigegeben ist oder nicht zu deinem Betrieb gehört',
    hint: 'Fahrzeug freigeben oder wechseln.',
  },
  driver_not_in_fleet: {
    remedy: REMEDY.PARTNER,
    text: 'gehört nicht (mehr) zu deinem Betrieb',
    hint: 'Fahrerliste prüfen.',
  },
  no_partner: {
    remedy: REMEDY.PARTNER,
    text: 'ist keinem Betrieb zugeordnet',
    hint: 'Fahrer dem Betrieb zuordnen.',
  },
  no_profile: {
    remedy: REMEDY.PARTNER,
    text: 'hat kein vollständiges Fahrerprofil',
    hint: 'Profil vervollständigen lassen.',
  },
  partner_not_approved: {
    remedy: REMEDY.PARTNER,
    text: 'kann nicht fahren, weil dein Betrieb noch nicht freigegeben ist',
    hint: 'Freigabe deines Betriebs abwarten bzw. Unterlagen nachreichen.',
  },
  partner_suspended: {
    remedy: REMEDY.PARTNER,
    text: 'kann nicht fahren, weil dein Betrieb gesperrt ist',
    hint: 'Gültige Genehmigung hochladen — danach wird der Betrieb automatisch entsperrt.',
  },
  partner_deleting: {
    remedy: REMEDY.PARTNER,
    text: 'kann nicht fahren, weil dein Betrieb in Kündigung ist',
    hint: 'Kündigung in den Einstellungen zurückziehen.',
  },

  // ── Es liegt am Zeitpunkt ───────────────────────────────────────────────────
  fleet_release_delayed: {
    remedy: REMEDY.TIME,
    text: 'sieht Flotten-Freigaben wegen seiner Statusstufe erst etwas später',
    hint: 'Kurz warten — oder ihm die Fahrt gezielt zuweisen, das gilt sofort.',
  },
  chain_conflict: {
    remedy: REMEDY.TIME,
    text: 'hat zu dieser Uhrzeit bereits eine Fahrt',
    hint: null,
  },
  arrival_deadline_missed: {
    remedy: REMEDY.TIME,
    text: 'schafft die Anfahrt bis zur Abholzeit nicht',
    hint: null,
  },

  // ── Es liegt gerade am System ───────────────────────────────────────────────
  routing_unavailable: {
    remedy: REMEDY.SYSTEM,
    text: 'konnte nicht geprüft werden — die Anfahrt ist gerade nicht berechenbar',
    hint: 'Gleich noch einmal freigeben.',
  },
  eligibility_unavailable: {
    remedy: REMEDY.SYSTEM,
    text: 'konnte nicht abschließend geprüft werden',
    hint: 'Gleich noch einmal freigeben.',
  },
  proof_budget_deferred: {
    remedy: REMEDY.SYSTEM,
    text: 'wurde in diesem Durchgang noch nicht geprüft',
    hint: 'Gleich noch einmal freigeben.',
  },
  not_list_eligible: {
    remedy: REMEDY.SYSTEM,
    text: 'ist aus einem nicht näher bestimmbaren Grund nicht berechtigt',
    hint: 'Wenn das bleibt, bitte den Support fragen.',
  },

  // ── Niemand ist da ──────────────────────────────────────────────────────────
  no_active_device: {
    remedy: REMEDY.ABSENT,
    text: 'ist gerade mit keinem Gerät erreichbar',
    hint: 'Fahrer bitten, die App zu öffnen und online zu gehen.',
  },
};

// Ein unbekannter Grund darf nie zu einer leeren Zeile führen. Er wird als System-Fall
// gezeigt UND behält seinen Code, damit der Support ihn nennen kann.
export function explainReason(code) {
  const known = REASONS[code];
  if (known) return { ...known, code, known: true };
  return {
    code,
    known: false,
    remedy: REMEDY.SYSTEM,
    text: `ist nicht berechtigt (${code || 'ohne Angabe'})`,
    hint: 'Wenn das bleibt, bitte den Support mit diesem Kürzel fragen.',
  };
}

export const KNOWN_REASON_CODES = Object.keys(REASONS);

// Aus `fleet_alert` die Gründe gruppieren — je Grund EINE Zeile mit Anzahl.
// Bewusst keine Fahrernamen und keine Fahrer-IDs: die Anzahl reicht, um zu handeln,
// und so kann hier grundsätzlich kein Fahrer eines fremden Betriebs auftauchen.
export function groupReasons(fleetAlert) {
  const counts = new Map();
  for (const entry of fleetAlert?.driver_alerts || []) {
    if (entry.alerted) continue;
    const code = entry.reason || null;
    counts.set(code, (counts.get(code) || 0) + 1);
  }
  const groups = [...counts.entries()].map(([code, count]) => ({ ...explainReason(code), count }));
  groups.sort((a, b) => (REMEDY_ORDER.indexOf(a.remedy) - REMEDY_ORDER.indexOf(b.remedy))
    || (b.count - a.count)
    || String(a.code).localeCompare(String(b.code)));
  return groups;
}

// Die Abdeckungs-Diagnose weiß eine Sache, die der Sammelruf nicht wissen kann: ob die
// Prüfung selbst gerade unsicher war (Routing-Ausfall, Zustandswechsel während der
// Prüfung). Das ist EINE zusätzliche Zeile — die Fahrer werden NICHT doppelt gezählt.
export function coverageNote(fleetCoverage) {
  const state = fleetCoverage?.state;
  if (state === 'indeterminate') {
    return 'Die Prüfung war gerade nicht eindeutig — gleich noch einmal freigeben.';
  }
  if (state === 'state_changed') {
    return 'Die Reservierung hat sich während der Prüfung geändert — die Liste unten zeigt den aktuellen Stand.';
  }
  return null;
}
